import { studioTool, type PublicationDto } from '@ads/shared';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { Doc } from '../doc/Doc';
import { dateLong, dateTime } from '../lib/format';
import { useArtifact, usePublications, useSession } from '../lib/queries';
import { PublishSheet } from '../site/Publish';
import { SiteCanvas } from '../site/SiteCanvas';

/* Uma peça, renderizada com o design system da versão que a gerou. */
export function Peca() {
  const { id } = useParams();
  const nav = useNavigate();
  const { me } = useSession();
  const q = useArtifact(id);
  const a = q.data;
  const t = a ? studioTool(a.tool) : null;
  useViewMeta({ nav: 'biblioteca', crumb: ['Biblioteca', t?.name ?? 'Peça'], j: .9 });
  const isPage = t?.output === 'page';
  const pubs = usePublications(isPage ? a?.id : null);
  const live = pubs.data?.find(p => p.status === 'done' && p.url);
  const [publishing, setPublishing] = useState(false);

  return (
    <section className="view on" data-v="peca">
      <button className="back" type="button" onClick={() => (window.history.state?.idx > 0 ? nav(-1) : nav('/biblioteca'))}><Ic.ChevronLeft />Voltar</button>
      {q.isError && <div className="notice"><Ic.Warning /><p>Esta peça não existe ou você não tem acesso a ela.</p></div>}
      {a && t && (
        <>
          <div className="peca-head">
            <div>
              <div className="eyebrow"><span className="rule" /><span>{t.name}</span><b>{a.status === 'ready' ? 'Pronta' : a.status === 'failed' ? 'Falhou' : 'Gerando'}</b></div>
              <p className="peca-meta">
                Gerada em {dateTime(a.createdAt)}{a.createdBy ? ` por ${a.createdBy}` : ''} · {a.body.length} blocos
                {a.designSystem ? ` · ${a.designSystem.name} ${a.designSystem.version}` : ''}
                {a.provider === 'mock' ? ' · roteiro de demonstração' : a.model ? ` · ${a.model}` : ''}
              </p>
            </div>
          </div>
          {a.status === 'failed' && <div className="notice warn"><Ic.Warning /><p>{a.error}</p></div>}
          {isPage ? (
            <>
              <div className="peca-site gen-stage site">
                <SiteCanvas title={a.title} orgName={a.org.name || me.org.name} blocks={a.body} ds={a.designSystem}
                  primaryLink={a.briefing.link} running={a.status === 'generating'}
                  host={live?.url ? live.url.replace(/^https?:\/\//, '').replace(/\/$/, '') : null}
                  openHref={a.status === 'ready' ? `/api/artifacts/${a.id}/site/` : null} />
              </div>
              {!!pubs.data?.length && <PubHistory list={pubs.data} />}
            </>
          ) : (
            <div className="peca-paper">
              <Doc title={a.title} org={a.org.name || me.org.name} deliverable={t.deliverable}
                date={dateLong(a.createdAt)} blocks={a.body} ds={a.designSystem} writing={a.status === 'generating'} />
            </div>
          )}
        </>
      )}

      <Actions>
        {!isPage && <button className="btn-ghost" type="button" onClick={() => window.print()}><span className="with-ic"><Ic.Print />Imprimir ou salvar PDF</span></button>}
        {t && isPage && <button className="btn-ghost" type="button" onClick={() => nav(`/studio/${t.key}`)}>Nova versão</button>}
        {t && isPage && a?.status === 'ready' && (
          <button className="btn-primary" type="button" onClick={() => setPublishing(true)}>
            <span className="with-ic"><Ic.Rocket />{live ? 'Publicar de novo' : 'Aprovar e publicar'}</span>
          </button>
        )}
        {t && !isPage && <button className="btn-primary" type="button" onClick={() => nav(`/studio/${t.key}`)}>Nova versão<Ic.ArrowRight /></button>}
      </Actions>
      {publishing && a && t && <PublishSheet artifactId={a.id} title={a.title} tool={t.key} onClose={() => setPublishing(false)} />}
    </section>
  );
}

function PubHistory({ list }: { list: PublicationDto[] }) {
  return (
    <div className="pub-hist">
      <div className="fam-h sm">Publicações</div>
      <ul>
        {list.slice(0, 6).map(p => (
          <li key={p.id}>
            <em data-s={p.status}>{p.status === 'done' ? (p.url ? 'no ar' : 'versionada') : p.status === 'failed' ? 'falhou' : 'publicando'}</em>
            <span className="ph-slug">{p.slug}</span>
            <span className="ph-when">{dateTime(p.createdAt)}{p.createdBy ? ` · ${p.createdBy}` : ''}</span>
            <span className="ph-act">
              {p.url && p.status === 'done' && <a href={p.url} target="_blank" rel="noopener"><Ic.External />Site</a>}
              {p.commitUrl && <a href={p.commitUrl} target="_blank" rel="noopener"><Ic.Branch />{p.commitSha?.slice(0, 7)}</a>}
            </span>
            {p.status === 'failed' && p.error && <span className="ph-err">{p.error}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
